import { useState, useEffect, useRef, useCallback } from "react";
import { avatarBg } from "@/lib/avatar";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { useAppMode } from "@/context/AppModeContext";
import { useLocale } from "@/i18n/LocaleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Send, Coins, Sparkles, X, Languages } from "lucide-react";
import { onRealtimeEvent } from "@/lib/realtime";

export default function Conversation() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appName } = useAppMode();
  const { t, lang } = useLocale();
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [convApp, setConvApp] = useState("luxe");
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [trMsgs, setTrMsgs] = useState({});
  const [trLoading, setTrLoading] = useState({});
  const messagesEndRef = useRef(null);

  // The payer of this conversation is set by the app it was started in
  const isConvPayer = user?.gender === (convApp === "dluxe" ? "female" : "male");
  const aiBrand = user?.gender === "female" ? "AI Cupid" : "AI Wingman";

  const handleSuggest = async () => {
    if (!otherUser || suggesting) return;
    setSuggesting(true);
    try {
      const response = await api.post("/chat/suggest-replies", {
        receiver_id: otherUser.user_id
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("prof.tSuggestFail"));
    } finally {
      setSuggesting(false);
    }
  };

  const handleTranslate = async (msg) => {
    const cur = trMsgs[msg.message_id];
    if (cur?.text) {
      setTrMsgs((p) => ({ ...p, [msg.message_id]: { ...cur, show: !cur.show } }));
      return;
    }
    setTrLoading((p) => ({ ...p, [msg.message_id]: true }));
    try {
      const res = await api.post("/translate", { texts: [msg.content], lang });
      const text = res.data.translations?.[0] || msg.content;
      setTrMsgs((p) => ({ ...p, [msg.message_id]: { text, show: true } }));
    } catch (error) {
      toast.error(t("chat.translateFail"));
    } finally {
      setTrLoading((p) => ({ ...p, [msg.message_id]: false }));
    }
  };

  const fetchMessages = useCallback(async () => {
    try {
      const response = await api.get(`/messages/${conversationId}`);
      setMessages(response.data.messages);
      
      // Get other user info from conversations
      const convResponse = await api.get("/messages/conversations");
      const conv = convResponse.data.conversations.find(c => c.conversation_id === conversationId);
      if (conv) {
        setOtherUser(conv.other_user);
        setConvApp(conv.app || "luxe");
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      if (error.response?.status === 404) {
        navigate("/chat");
      }
    } finally {
      setLoading(false);
    }
  }, [conversationId, navigate]);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 5 seconds (fallback when WebSocket is unavailable)
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    // Real-time: append new messages instantly via WebSocket
    const unsubscribe = onRealtimeEvent((event) => {
      if (event.type === "new_message" && event.message?.conversation_id === conversationId) {
        setMessages((prev) =>
          prev.some((m) => m.message_id === event.message.message_id)
            ? prev
            : [...prev, event.message]
        );
      }
    });
    return unsubscribe;
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !otherUser) return;

    setSending(true);
    try {
      const response = await api.post("/messages/send", {
        receiver_id: otherUser.user_id,
        content: newMessage.trim(),
        conversation_id: conversationId
      });
      
      setNewMessage("");
      fetchMessages();
      
      if (response.data.credits_spent > 0) {
        toast.success(t("chat.sentCredits", { n: response.data.credits_spent }));
      }
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t("chat.noCredits"));
        navigate("/credits");
      } else {
        toast.error(t("prof.tMsgFail"));
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="conversation-page">
      {/* Header */}
      <header className="glass sticky top-0 z-30 px-4 py-3 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/chat")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        {otherUser && (
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate(`/profile/${otherUser.user_id}`)}
          >
            <img
              src={otherUser.photos?.[0] || otherUser.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherUser.name)}&background=${avatarBg()}&color=fff&size=100`}
              alt={otherUser.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-semibold">{otherUser.name}</h2>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${convApp === "dluxe" ? "border-rose-500/40 text-rose-400" : "border-violet-500/40 text-violet-400"}`}
                  data-testid="conversation-app-badge"
                >
                  {convApp === "dluxe" ? "DLuxe" : "LuxeMeet"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{otherUser.location || t("chat.member", { app: appName })}</p>
            </div>
          </div>
        )}
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            <p>{t("chat.noMessages")}</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[75%] md:max-w-[520px] px-4 py-3 rounded-2xl ${
                  msg.sender_id === user?.user_id
                    ? "bg-primary text-white rounded-br-md"
                    : "bg-white/10 rounded-bl-md"
                }`}
              >
                <p className="leading-relaxed whitespace-pre-line">
                  {msg.sender_id !== user?.user_id && trMsgs[msg.message_id]?.show
                    ? trMsgs[msg.message_id].text
                    : msg.content}
                </p>
                <div className={`flex items-center gap-3 mt-1 ${
                  msg.sender_id === user?.user_id ? "justify-end" : ""
                }`}>
                  <p className={`text-xs ${
                    msg.sender_id === user?.user_id ? "text-white/60" : "text-muted-foreground"
                  }`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {msg.sender_id !== user?.user_id && (
                    <button
                      type="button"
                      onClick={() => handleTranslate(msg)}
                      disabled={trLoading[msg.message_id]}
                      className="flex items-center gap-1 text-[13px] font-semibold text-violet-300 hover:text-violet-200 underline underline-offset-2 transition-colors"
                      data-testid={`translate-msg-btn-${msg.message_id}`}
                    >
                      <Languages className="w-3 h-3" />
                      {trLoading[msg.message_id]
                        ? t("chat.translating")
                        : trMsgs[msg.message_id]?.show
                        ? t("chat.showOriginal")
                        : t("chat.translate")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message input */}
      <div className="glass sticky bottom-0 px-4 py-4">
        {suggestions.length > 0 && (
          <div className="mb-3" data-testid="ai-suggestions">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary" /> {t("chat.tapToUse", { ai: aiBrand })}
              </p>
              <button
                type="button"
                onClick={() => setSuggestions([])}
                className="text-muted-foreground hover:text-white"
                data-testid="dismiss-suggestions-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="text-left text-sm px-4 py-2.5 rounded-xl bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors"
                  onClick={() => {
                    setNewMessage(s);
                    setSuggestions([]);
                  }}
                  data-testid={`ai-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {isConvPayer && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 justify-center">
            <Coins className="w-3 h-3" />
            <span>{messages.length === 0 ? t("chat.costStart") : t("chat.costMessage")}</span>
          </div>
        )}
        <form onSubmit={handleSend} className="flex gap-3">
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="w-12 h-12 rounded-full border-primary/30 text-primary hover:bg-primary/10 shrink-0"
            onClick={handleSuggest}
            disabled={suggesting || !otherUser}
            title={`${aiBrand} suggestions`}
            data-testid="ai-suggest-btn"
          >
            {suggesting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary"></div>
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
          </Button>
          <Input
            placeholder={t("chat.typeMessage")}
            className="flex-1 h-12 bg-white/5 border-white/10 rounded-full px-5"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={sending}
            data-testid="message-input"
          />
          <Button
            type="submit"
            size="icon"
            className="w-12 h-12 rounded-full bg-primary hover:bg-primary/90"
            disabled={sending || !newMessage.trim()}
            data-testid="send-btn"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
